import { InboxOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Divider, Form, Input, Upload } from 'antd';
import React, { useEffect, useState } from 'react';
import { openNotification } from '../../../common/utils';
import ModalComponent from '../../../components/ModalComponent';
import SelectComponent from '../../../components/SelectComponent';
import useS3FileUpload, {
  UPLOAD_FAILED_PROGRESS,
} from '../../../hooks/useS3FileUpload';
import { CREATE_MEDIA_UPLOADS } from '../graphql/Mutations';
import { GET_MEDIA_UPLOAD_SIGNED_PUT_URL } from '../graphql/Queries';

const { Item } = Form;
const { Dragger } = Upload;

const UploadMediaModal = ({
  isModalOpen,
  setIsModalOpen,
  onUploaded,
  typeOptions,
  getSubtypeOptions,
  isOptionsLoading,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { uploadFile, progress, resetProgress } = useS3FileUpload();

  const subtypeOptions = getSubtypeOptions(selectedType);

  useEffect(() => {
    if (!isModalOpen) {
      form?.resetFields();
      setFileList([]);
      setSelectedType(null);
      resetProgress();
    }
  }, [isModalOpen]);

  const getFileStatus = (file) => {
    const filePercent = progress?.[file?.uid];
    if (filePercent === undefined) return file?.status;
    if (filePercent === UPLOAD_FAILED_PROGRESS) return 'error';
    if (filePercent === 100) return 'done';
    return 'uploading';
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
    form?.setFieldValue('subtype', undefined);
  };

  const [getMediaUploadSignedPutUrl] = useLazyQuery(
    GET_MEDIA_UPLOAD_SIGNED_PUT_URL,
    {
      fetchPolicy: 'network-only',
    },
  );

  const [createMediaUploads] = useMutation(CREATE_MEDIA_UPLOADS, {
    onError: () => {},
  });

  const getSignedPutUrl = async (fileName) => {
    const response = await getMediaUploadSignedPutUrl({
      variables: {
        data: {
          fileNames: [fileName],
          type: form?.getFieldValue('type'),
          subtype: form?.getFieldValue('subtype') || null,
        },
      },
    });
    return response?.data?.getMediaUploadSignedPutUrl?.[0];
  };

  const handleSubmit = async (values) => {
    const file = fileList?.[0];
    if (!file) {
      openNotification('error', 'Please select a file to upload.');
      return;
    }
    setIsSubmitting(true);
    try {
      const signedData = await uploadFile(file?.originFileObj, getSignedPutUrl);
      const response = await createMediaUploads({
        variables: {
          data: {
            type: values?.type,
            subtype: values?.subtype || null,
            files: [
              {
                fileKey: signedData?.fileName,
                originalFileName: signedData?.originalFileName || file?.name,
                name: values?.name?.trim(),
              },
            ],
          },
        },
      });
      if (!response?.data) {
        throw new Error();
      }
      setIsModalOpen(false);
      await onUploaded();
    } catch (error) {
      if (error?.message) {
        openNotification('error', error?.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalComponent
      open={isModalOpen}
      title="Upload Media"
      okText="Upload"
      onOk={form?.submit}
      onCancel={() => !isSubmitting && setIsModalOpen(false)}
      confirmLoading={isSubmitting}
      cancelButtonProps={{ disabled: isSubmitting }}
      closable={!isSubmitting}
      keyboard={false}
      destroyOnClose
      forceRender
    >
      <Divider />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        disabled={isSubmitting}
      >
        <Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please enter a name!' }]}
        >
          <Input allowClear placeholder="Enter a name for this media" />
        </Item>
        <Item
          label="Type"
          name="type"
          rules={[{ required: true, message: 'Please select a type!' }]}
        >
          <SelectComponent
            optionFilterProp="label"
            placeholder="Select type"
            options={typeOptions}
            loading={isOptionsLoading}
            onChange={handleTypeChange}
          />
        </Item>
        <Item label="Subtype" name="subtype">
          <SelectComponent
            optionFilterProp="label"
            placeholder={
              selectedType ? 'Select subtype' : 'Select a type first'
            }
            options={subtypeOptions}
            disabled={isSubmitting || !selectedType}
          />
        </Item>
        <Item label="File" required>
          <Dragger
            multiple={false}
            maxCount={1}
            beforeUpload={() => false}
            disabled={isSubmitting}
            fileList={fileList?.map((file) => ({
              ...file,
              status: getFileStatus(file),
              percent: progress?.[file?.uid] ?? 0,
            }))}
            onChange={({ fileList: updatedFileList }) =>
              setFileList(updatedFileList?.slice(-1))
            }
            onRemove={() => !isSubmitting}
            showUploadList={{ showRemoveIcon: !isSubmitting }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag a file here</p>
            <p className="ant-upload-hint">One file at a time.</p>
          </Dragger>
        </Item>
      </Form>
    </ModalComponent>
  );
};

export default UploadMediaModal;
